import { useEffect, useState } from 'react'
import { ChevronDown, ShieldCheck, Zap, Users, Globe } from 'lucide-react'
import { Shell } from '../components/Layout'
import { Card, SectionHead, Rows, Badge } from '../components/UI'
import { get } from '../lib/api'
import { clsx, fa, num, asArray} from '../lib/format'
import { useSeo } from '../lib/seo'

const PAGE_SEO = {
  faq: {
    title: 'سؤالات متداول',
    description: 'پاسخ پرتکرارترین پرسش‌ها درباره ثبت‌نام، احراز هویت، واریز و برداشت، کارمزد و امنیت در صرافی ارزینو.',
    keywords: 'سوالات متداول صرافی، احراز هویت، برداشت ریالی',
    canonical: '/faq',
  },
  fees: {
    title: 'کارمزد معاملات و برداشت',
    description: 'جدول شفاف کارمزد معاملات پلکانی، تبدیل ارز و برداشت ریالی و رمزارزی در ارزینو.',
    keywords: 'کارمزد صرافی، کارمزد معامله، کارمزد برداشت',
    canonical: '/fees',
  },
  about: {
    title: 'درباره ارزینو',
    description: 'ارزینو بستری امن برای خرید، فروش و نگهداری ارزهای دیجیتال با کیف پول سرد چندامضایی و پشتیبانی شبانه‌روزی.',
    keywords: 'درباره ارزینو، صرافی معتبر ارز دیجیتال',
    canonical: '/about',
  },
} as const

function Faq() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [open, setOpen] = useState<number | null>(0)
  useEffect(() => { get('/content/faq').then((r: any) => setRows(asArray(r))).catch(() => setRows([])) }, [])

  // داده ساختاریافته پرسش‌وپاسخ — همان چیزی که باعث می‌شود گوگل پرسش‌ها را
  // مستقیم زیر نتیجه سایت نشان دهد.
  useSeo(rows && rows.length ? {
    ...PAGE_SEO.faq,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: rows.map((f: any) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  } : PAGE_SEO.faq, [rows?.length])

  return (
    <>
      <SectionHead title="سؤالات متداول" subtitle="پاسخ پرتکرارترین پرسش‌های کاربران ارزینو" />
      {rows === null ? <Rows n={5} /> : (
        <div className="mx-auto max-w-3xl space-y-3">
          {rows.map((f, i) => (
            <Card key={i} className="!p-0">
              <button onClick={() => setOpen(open === i ? null : i)}
                className="flex w-full items-center justify-between gap-4 p-5 text-right">
                <span className="text-sm font-semibold text-white">{f.q}</span>
                <ChevronDown className={clsx('h-4 w-4 shrink-0 text-slate-500 transition-transform', open === i && 'rotate-180')} />
              </button>
              {open === i && <p className="border-t border-white/[.06] p-5 text-sm leading-8 text-slate-400">{f.a}</p>}
            </Card>
          ))}
        </div>
      )}
    </>
  )
}

function Fees() {
  const [data, setData] = useState<any>(null)
  useEffect(() => { get('/content/fees').then(setData).catch(() => {}) }, [])
  useSeo(PAGE_SEO.fees, [])

  return (
    <>
      <SectionHead title="کارمزدها" subtitle="ساختار شفاف کارمزد معاملات، تبدیل و برداشت" />
      {!data ? <Rows n={5} /> : (
        <div className="space-y-5">
          <Card className="!p-0">
            <div className="border-b border-white/[.06] p-4"><h3 className="font-bold text-white">کارمزد معاملات (پلکانی)</h3></div>
            {/* موبایل: هر پله کارمزد یک کارت */}
            <div className="space-y-2 p-3 md:hidden">
              {data.trading.map((r: any, i: number) => (
                <div key={r.level} className="rounded-xl border border-white/[.06] bg-white/[.02] p-3">
                  <div className="mb-1.5 flex items-center gap-2">
                    <Badge kind={i === 3 ? 'gold' : i === 0 ? 'muted' : 'info'}>{r.level}</Badge>
                    <span className="text-[11px] text-slate-400">{r.volume}</span>
                  </div>
                  <div className="flex gap-4 text-xs">
                    <span className="text-slate-500">سازنده <b className="num text-up">{num(r.maker, 2)}٪</b></span>
                    <span className="text-slate-500">بازارگیر <b className="num text-brand-200">{num(r.taker, 2)}٪</b></span>
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden overflow-x-auto p-2 md:block">
              <table className="w-full min-w-[520px] text-sm">
                <thead><tr className="text-xs text-slate-500">
                  {['سطح', 'حجم معاملات ۳۰ روزه (تومان)', 'سازنده (Maker)', 'بازارگیر (Taker)'].map((h) =>
                    <th key={h} className="px-4 py-3 text-right font-medium">{h}</th>)}
                </tr></thead>
                <tbody>
                  {data.trading.map((r: any, i: number) => (
                    <tr key={r.level} className="border-t border-white/[.05]">
                      <td className="px-4 py-3"><Badge kind={i === 3 ? 'gold' : i === 0 ? 'muted' : 'info'}>{r.level}</Badge></td>
                      <td className="px-4 py-3 text-slate-300">{r.volume}</td>
                      <td className="num px-4 py-3 text-up">{num(r.maker, 2)}٪</td>
                      <td className="num px-4 py-3 text-brand-200">{num(r.taker, 2)}٪</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="grid gap-5 md:grid-cols-2">
            <Card>
              <h3 className="mb-3 font-bold text-white">کارمزد تبدیل سریع</h3>
              <div className="num text-3xl font-black text-brand-300">{num(data.convert, 2)}٪</div>
              <p className="mt-2 text-xs leading-6 text-slate-400">برای هر تبدیل مستقیم ارز به ارز، بدون هزینه پنهان.</p>
            </Card>
            <Card>
              <h3 className="mb-3 font-bold text-white">کارمزد برداشت</h3>
              <div className="space-y-2 text-xs">
                {Object.entries(data.withdraw).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-white/[.05] pb-2 last:border-0">
                    <span className="num text-slate-400" dir="ltr">{k}</span>
                    <span className="text-slate-200">{String(v)}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}
    </>
  )
}

function About() {
  useSeo(PAGE_SEO.about, [])
  const stats = [
    { icon: Users, label: 'کاربر فعال', value: `${fa('۴۲۸')} هزار` },
    { icon: Globe, label: 'ارز قابل معامله', value: `${fa('۲۲')}+` },
    { icon: Zap, label: 'میانگین زمان اجرای سفارش', value: `${fa('۹۰')} میلی‌ثانیه` },
    { icon: ShieldCheck, label: 'آپ‌تایم سرویس', value: `${fa('۹۹٫۹۸')}٪` },
  ]
  return (
    <>
      <SectionHead title="درباره ارزینو" subtitle="ماموریت ما ساده‌کردن دسترسی ایرانیان به بازار جهانی رمزارز است" />
      <Card className="mb-5 !p-8">
        <p className="max-w-3xl text-sm leading-9 text-slate-300">
          ارزینو با هدف ایجاد بستری امن، سریع و قابل اعتماد برای خرید و فروش ارزهای دیجیتال شکل گرفت.
          ما باور داریم که دسترسی به بازارهای مالی جهانی حق همه است و این دسترسی باید ساده، شفاف و بدون
          پیچیدگی‌های غیرضروری باشد. تیم ارزینو متشکل از متخصصان امنیت، مهندسان نرم‌افزار و کارشناسان
          بازارهای مالی است که شبانه‌روز برای پایداری و امنیت پلتفرم تلاش می‌کنند.
        </p>
      </Card>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s) => (
          <Card key={s.label} hover>
            <s.icon className="mb-3 h-6 w-6 text-brand-300" />
            <div className="num text-2xl font-extrabold text-white">{s.value}</div>
            <div className="mt-1 text-xs text-slate-500">{s.label}</div>
          </Card>
        ))}
      </div>
      <Card className="mt-5">
        <h3 className="mb-4 font-bold text-white">قوانین و شرایط استفاده</h3>
        <ul className="space-y-3 text-sm leading-8 text-slate-400">
          <li>• استفاده از خدمات ارزینو مستلزم تکمیل فرآیند احراز هویت مطابق قوانین جاری است.</li>
          <li>• حساب کاربری غیرقابل انتقال است و استفاده از حساب دیگران ممنوع می‌باشد.</li>
          <li>• مسئولیت تصمیم‌های معاملاتی و ریسک نوسانات بازار بر عهده کاربر است.</li>
          <li>• برداشت ریالی تنها به حساب‌های بانکی به نام صاحب حساب کاربری انجام می‌شود.</li>
          <li>• ارزینو هیچ‌گاه رمز عبور یا کد دومرحله‌ای شما را درخواست نمی‌کند.</li>
        </ul>
      </Card>
    </>
  )
}

export default function Info({ page }: { page: 'faq' | 'fees' | 'about' }) {
  return <Shell>{page === 'faq' ? <Faq /> : page === 'fees' ? <Fees /> : <About />}</Shell>
}
